import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonGrid,
  IonCol,
  IonFabButton,
  IonSpinner,
  IonRow
} from '@ionic/react';
import './SetPatient.css';
import { AppointmentStepProps } from '../../navigators/AppointmentNavigator';
import Stepper from '../../components/Stepper';
import AppHeader from '../../components/AppHeader';
import NewPatientForm from '../../components/NewPatientForm';
import { Appointment, Patient } from '../../models';
import { setAppointment } from '../../store/actions/appointments';
import { connect } from 'react-redux';
import { Switch, Route, useLocation } from 'react-router-dom';
import { AuthenticationRootState } from '../../store/reducers/authentication';
import PatientRepository from '../../repositories/PatientRepository';
import { useSelector } from 'react-redux';

const mapStateToProps = (
  state: { authentication: AuthenticationRootState },
  ownProps: any
) => {
  return {
    user: state.authentication.user,
    isLoggedIn: !!state.authentication.accessToken
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  updateAppointment: (appointment: Partial<Appointment>) =>
    dispatch(setAppointment(appointment))
});

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  AppointmentStepProps;

const SetPatient: React.FC<Props> = ({
  step,
  nextStepRouterLink = '/',
  updateAppointment,
  history,
  isLoggedIn,
  user,
  match,
  stepperComponent = <Stepper step={step} />
}) => {
  const [isPatientsLoading, setIsPatientsLoading] = useState(true);
  const [availablePatients, setAvailablePatients] = useState<Patient[]>([]);
  const location = useLocation();

  const labsIdWithPrescriptionScanner =
    process.env.REACT_APP_LAB_IDS_WITH_PRESCRIPTION_SCANNER || '';

  let labsIdWithPrescriptionScannerArray: number[] = [];
  if (labsIdWithPrescriptionScanner.trim().length > 0) {
    labsIdWithPrescriptionScannerArray = labsIdWithPrescriptionScanner
      .split(',')
      .map(Number);
  }

  const currentLaboratory = useSelector(
    (state: any) => state.appointments.appointment.laboratory
  );

  const nextStep = labsIdWithPrescriptionScannerArray.includes(
    currentLaboratory?.id
  )
    ? 'verification-ordonnance'
    : nextStepRouterLink;

  useEffect(() => {
    let isRendered = true;

    async function fetchPatient() {
      if (user?.id) {
        setIsPatientsLoading(true);
        const allPatient = await PatientRepository.findAll();
        setAvailablePatients(
          allPatient.filter((patient) => patient.id !== user.patient.id)
        );
        if (isRendered) {
          setIsPatientsLoading(false);
        }
      }
    }

    fetchPatient();

    return () => {
      isRendered = false;
    };
  }, [user]);

  function setPatient(patient: Patient) {
    updateAppointment({
      patient
    });
    history.push(nextStep);
  }

  function handleForMeButtonClicked(patient: Patient) {
    setPatient(patient);
  }

  function handleForMeButtonClickedLoggedIn() {
    history.push(nextStep);
  }

  function handleNewPatientButtonClicked() {
    history.push(`${match.url.replace(/\/$/, '')}/ajouter`);
  }

  async function onNewPatient(patient: Patient) {
    setPatient(patient);
  }

  return (
    <IonPage>
      <AppHeader defaultBackLink="/prendre-rendez-vous/laboratoire" />
      {stepperComponent}
      <IonContent>
        <Switch location={location}>
          <Route path={match.url} exact>
            <div className="ion-padding ion-text-center wrapper wrapper--large wrapper--centered">
              <div>
                {isLoggedIn ? (
                  <>
                    <IonFabButton
                      className="large initials"
                      onClick={() => {
                        handleForMeButtonClicked(user!.patient);
                      }}
                    >
                      {user?.getInitials()}
                    </IonFabButton>
                    <div>
                      <strong>Pour moi</strong>
                      <br />
                      {user?.getFullName()}
                    </div>
                  </>
                ) : (
                  <>
                    <IonFabButton
                      className="large initials"
                      onClick={() => {
                        updateAppointment({
                          patient: undefined
                        });
                        handleForMeButtonClickedLoggedIn();
                      }}
                    >
                      Moi
                    </IonFabButton>
                    <div>
                      <strong>Pour moi</strong>
                    </div>
                  </>
                )}
              </div>
              <IonGrid className="other-patients">
                {isLoggedIn ? (
                  isPatientsLoading ? (
                    <IonSpinner />
                  ) : (
                    availablePatients.map((patient) => (
                      <IonCol size="4" key={patient.id}>
                        <IonFabButton
                          className="initials"
                          onClick={() => handleForMeButtonClicked(patient)}
                        >
                          {patient.getInitials()}
                        </IonFabButton>
                        <div>{patient.getFullName()}</div>
                      </IonCol>
                    ))
                  )
                ) : null}
                <IonCol size="4">
                  <IonFabButton
                    className="initials neo"
                    onClick={() => {
                      handleNewPatientButtonClicked();
                    }}
                    routerDirection="forward"
                  >
                    +
                  </IonFabButton>
                  <div>
                    <strong>Pour un proche</strong>
                  </div>
                </IonCol>
              </IonGrid>
            </div>
          </Route>
          <Route path={`${match.url.replace(/\/$/, '')}/ajouter`}>
            <div className="ion-padding wrapper wrapper--large">
              <IonRow>
                <IonCol>
                  <h2 className="ion-text-center">Ajouter un proche</h2>
                </IonCol>
              </IonRow>
              <NewPatientForm
                onConfirmForm={(patient: Patient) => onNewPatient(patient)}
                returnOnly={!isLoggedIn}
              />
            </div>
          </Route>
        </Switch>
      </IonContent>
    </IonPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SetPatient);
